import IJobSection from "../interfaces/IJobSection";
import { IFrontendResults } from "../interfaces/frontend/IFrontendResults";
import { useMemo } from "react";
import { Uniprot } from "../../../api/openapi-generator";

import t from "../../../data/tooltips/tooltips.json";

export function useAvailableSections(feResults: IFrontendResults | null, uniprot: Uniprot | null) {
  return useMemo<IJobSection[]>(() => {
    if (!feResults) return [];

    // Adjust sections:
    // - Set summary section name
    // - Remove inhibitor sections if inhibitors are not enabled
    // - Remove catalytic sections if pka is not enabled

    // Hide catalytic residues: apart from !allowPka, hide also if there are no changes in the end
    // This is problematic a little bit, as Catalytic residues section would disappear after job is finished,
    // but at least 'Catalytic residues - No changes in pKa' is displayed in the summary

    let sections: IJobSection[] = [
      deriveSummarySection(feResults.proteinCode, feResults.mutationStr),
      ...staticSections,
    ];

    const f = {allowPka: true, allowInhibitors: true, allowHope: true};  // TODO

    const hideCatalyticResidues = !f.allowPka || feResults.catalyticResidues !== null && feResults.catalyticResidues.length === 0;
    const hideKnownMutants = uniprot?.naturalVariants?.length === 0 && uniprot?.experimentalMutants?.length === 0;
    const hideStructure3d = !feResults.structure;

    return sections
      .filter(({id}) => f.allowInhibitors || !['inhibitor_list', 'inhibitor_chart', 'inhibitor_binding'].includes(id))
      .filter(({id}) => f.allowHope || !['mutant_description', 'conservation'].includes(id))
      .filter(({id}) => !hideCatalyticResidues || id !== 'catalytic_residues')
      .filter(({id}) => !hideKnownMutants || id !== 'known_mutants')
      .filter(({id}) => !hideStructure3d || id !== 'structure_3d')
  }, [feResults]);
}

function deriveSummarySection(proteinCode: string, mutationStr: string): IJobSection {
  const initSummarySection: IJobSection = {
    id: 'summary',
    name: 'Summary',
    tooltipHTML: null,
    disabled: true,
    loading: false,
  }
  const summaryForWhat = [proteinCode, mutationStr].filter(value => value !== null).join(' ');
  const name = summaryForWhat !== '' ? `${initSummarySection.name} for ${summaryForWhat}` : initSummarySection.name; // Summary for <protein> <mutation>
  return {
    ...initSummarySection,
    name,
  };
}

const staticSections: IJobSection[] = [
  {
    id: 'mutant_description',
    name: 'Mutant description',
    tooltipHTML: t.mutant_description,
    disabled: false,
    loading: false,
  },
  {
    id: 'known_mutants',
    name: 'Known mutants',
    tooltipHTML: false,  // no tip needed
    disabled: false,
    loading: false,
  },
  {
    id: 'catalytic_residues',
    name: 'Catalytic residues',
    tooltipHTML: t.catalytic_residues,
    disabled: false,
    loading: false,
  },
  {
    id: 'conservation',
    name: 'Conservation',
    tooltipHTML: t.conservation,
    disabled: false,
    loading: false,
  },
  {
    id: 'structure_3d',
    name: '3D visualization',
    tooltipHTML: false,  // no tip needed
    disabled: false,
    loading: false,
    screenOnly: true,
  },
  {
    id: 'inhibitor_list',
    name: 'Inhibitors', // (∆Ebound in kcal/mol)
    tooltipHTML: t.inhibitors,
    disabled: false,
    loading: false,
  },
  {
    id: 'inhibitor_chart',
    name: 'Top scoring inhibitor chart',
    tooltipHTML: t.inhibitor_chart,
    disabled: false,
    loading: false,
  },
  {
    id: 'additional_information',
    name: 'Additional information',
    tooltipHTML: false,  // no tip needed
    disabled: false,
    loading: false,
  },
]
